<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import GroupedSelect from '../../../../components/ui/form/GroupedSelect.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'
import PhoneInput from '../../../../components/ui/form/PhoneInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import { ref, onMounted, nextTick } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import WarehouseShelfsListView from './shelfs/WarehouseShelfsListView.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  id: props.data?.id || "",
    logo: props.data?.logo || "",
    code: props.data?.code || "",
    name: props.data?.name || props.listCreateName || "",
    prefix: props.data?.prefix || "",
    suffix: props.data?.suffix || "",
    is_outlet: props.data?.is_outlet || false,
    shelf_managed: props.data?.shelf_managed || false,
    is_transit: props.data?.is_transit || false,
    address: props.data?.address || "",
    buildings_id: props.data?.buildings_id || "",
    inventory_account_id: props.data?.inventory_account_id || "",
    lat: props.data?.lat || "",
    lng: props.data?.lng || "",
    tel: props.data?.tel || "",
    mobile: props.data?.mobile || "",
    viber: props.data?.viber || "",
    whatsapp: props.data?.whatsapp || "",
    email: props.data?.email || "",
    url: props.data?.url || "",
    opening_hours: props.data?.opening_hours || "",
    letterhead: props.data?.letterhead || "",
    active: props.data?.active || true,
    uid: props.data?.uid || "",
    shelfs: props.data?.shefs || []
  });

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("Name is required")]);
setFieldRules("mobile", [required("Mobile No is required")]);
setFieldRules("inventory_account_id", [required("Inventory Account is required")]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const shelfs = ref([])
const getRecord = async () => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    {
      id: props.data.uid,
    }
  );
  if (response.success == true) {
    setFormData(response.data);
    console.log(formData.value)
    nextTick(() => {
      console.log(response.data.shelfs)
      shelfs.value = response.data.shelfs
      console.log('shelfs.value', shelfs.value)
    })    
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  console.log("mounted => warehouse form", props.permissions)
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      getRecord()
    }
})


</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions" width="50rem"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
      <div class="grid md:grid-cols-3 gap-6">
        <TextInput
          v-model="fields.code.value"
          label="Code"
          placeholder="Enter Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />

        <TextInput
          v-model="fields.name.value"
          label="Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <TextInput
          v-model="fields.prefix.value"
          label="Prefix"
          :readonly="isReadOnly"
          :error="fields.prefix.touched ? fields.prefix.error : null"
          @blur="fields.prefix.touched = true"
        />

        <TextInput
          v-model="fields.suffix.value"
          label="Suffix"
          :readonly="isReadOnly"
          :error="fields.suffix.touched ? fields.suffix.error : null"
          @blur="fields.suffix.touched = true"
        />

        <CheckInput
          v-model="fields.is_outlet.value"
          :readonly="true"
          v-if="mode != 'create'"
          label="Is Outlet"
          :error="fields.is_outlet.touched ? fields.is_outlet.error : null"
          @blur="fields.is_outlet.touched = true"
        />

        <CheckInput
          v-model="fields.shelf_managed.value"
          :readonly="isReadOnly"
          label="Shelf Managed"
          :error="
            fields.shelf_managed.touched ? fields.shelf_managed.error : null
          "
          @blur="fields.shelf_managed.touched = true"
        />

        <CheckInput
          v-model="fields.is_transit.value"
          :readonly="true"
          label="Transit Warehouse"
          :error="
            fields.is_transit.touched ? fields.is_transit.error : null
          "
          @blur="fields.is_transit.touched = true"
        />

        <TextInput
          v-model="fields.address.value"
          label="Address"
          :readonly="isReadOnly"
          :error="fields.address.touched ? fields.address.error : null"
          @blur="fields.address.touched = true"
        />

        <GroupedSelect
          v-model="fields.buildings_id.value"
          label="Building"
          end-point="system/master-data/buildings"
          optionLabel="buildings"
          :readonly="isReadOnly"
          :error="
            fields.buildings_id.touched ? fields.buildings_id.error : null
          "
          @blur="fields.buildings_id.touched = true"
        />

        <NumberInput
          v-model="fields.lat.value"
          label="Latitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lat.touched ? fields.lat.error : null"
          @blur="fields.lat.touched = true"
        />

        <NumberInput
          v-model="fields.lng.value"
          label="Longitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lng.touched ? fields.lng.error : null"
          @blur="fields.lng.touched = true"
        />

        <PhoneInput
          v-model="fields.tel.value"
          label="Phone"
          :readonly="isReadOnly"
          :error="fields.tel.touched ? fields.tel.error : null"
          @blur="fields.tel.touched = true"
        />

        <PhoneInput
          v-model="fields.mobile.value"
          label="Mobile"
          :readonly="isReadOnly"
          :error="fields.mobile.touched ? fields.mobile.error : null"
          @blur="fields.mobile.touched = true"
        />

        <PhoneInput
          v-model="fields.viber.value"
          label="Viber"
          :readonly="isReadOnly"
          :error="fields.viber.touched ? fields.viber.error : null"
          @blur="fields.viber.touched = true"
        />

        <PhoneInput
          v-model="fields.whatsapp.value"
          label="WhatsApp"
          :readonly="isReadOnly"
          :error="fields.whatsapp.touched ? fields.whatsapp.error : null"
          @blur="fields.whatsapp.touched = true"
        />

        <TextInput
          v-model="fields.email.value"
          label="Email"
          :readonly="isReadOnly"
          :error="fields.email.touched ? fields.email.error : null"
          @blur="fields.email.touched = true"
        />

        <TextInput
          v-model="fields.url.value"
          label="URL"
          :readonly="isReadOnly"
          :error="fields.url.touched ? fields.url.error : null"
          @blur="fields.url.touched = true"
        />

        <TextInput
          v-model="fields.opening_hours.value"
          label="Name"
          :readonly="isReadOnly"
          :error="
            fields.opening_hours.touched ? fields.opening_hours.error : null
          "
          @blur="fields.opening_hours.touched = true"
        />

        <SearchSelect v-model="fields.inventory_account_id.value" end-point="finance/accounting/chart-of-accounts"
          label="Inventory Account" :allow-new-record="true" :readonly="isReadOnly"
          :error="fields.inventory_account_id.touched ? fields.inventory_account_id.error : null"
          @blur="fields.inventory_account_id.touched = true"/>

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>

      <WarehouseShelfsListView v-if="formData.shelf_managed == true" :data="shelfs" :permissions="permissions" :warehouse="formData"/>

    </template>
  </ModalFormTemplate>
</template>